// @flow
import { ApiClient, EvaluationApi, EvaluationBatchRequest } from 'flagr';
import { find, propEq, curry } from 'ramda';
import type { User } from './auth';
import { reduce } from '../helpers/utils';
import config from '../../config/config';

const debug = require('debug')('jp:stores:features');

ApiClient.instance.basePath = config.featureApiEndpoint;

const LOAD = 'features/LOAD';
const LOAD_SUCCESS = 'features/LOAD_SUCCESS';
const LOAD_FAIL = 'features/LOAD_FAIL';

type Flag = {| id: number, name: string |};
type Flags = Array<Flag>;

export type Feature = {|
  key: string,
  enabled: boolean,
  segmentID?: ?number,
  variantID?: ?number,
  variantKey?: ?string,
  variantAttachment?: ?Object
|};

type RawFeature = {|
  flagID: number,
  segmentID: ?number,
  variantID: ?number,
  variantKey: ?string,
  variantAttachment: Object,
  evalContext: {
    entityType: string,
    flagID: number,
    entityID: string,
    entityContext: Object
  },
  timestamp: string,
  flagSnapshotID: number
|};

type State = {|
  loaded: boolean,
  loading: boolean,
  features: Array<Feature>
|};

const FLAGS: Flags = [{ id: 3, name: 'listing_details_button' }];

const flagIDs = FLAGS.map((flag: Flag) => flag.id);

const findBy = curry((propName: string, propValue: any) =>
  find(propEq(propName, propValue))
);
const findFlagById = findBy('id');
const findFeatureByKey = findBy('key');

const isRawFeatureEnabled = (feature: RawFeature): boolean =>
  feature.segmentID !== null &&
  feature.variantID !== null &&
  feature.variantKey !== null;

const STATIC_FEATURES = [
  {
    key: 'primary_header_invest_link',
    enabled: false,
    segmentID: null,
    variantID: null,
    variantKey: null
  }
];

const initialState: State = {
  loaded: false,
  loading: false,
  features: STATIC_FEATURES
};

type GlobalState = { features: State };

type Action =
  | {| type: typeof LOAD |}
  | {| type: typeof LOAD_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_FAIL, payload: Object |}
  | {|
      types: Array<string>,
      params?: Object,
      promise: (?ApiClient) => Promise<{}>
    |};

export default reduce(initialState, {
  [LOAD](state: State): State {
    debug(LOAD);
    return { ...state, loading: true };
  },
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, action);
    if (!action.payload && !action.payload.evaluationResults) {
      return state;
    }

    const features = action.payload.evaluationResults
      .map(
        (feature: RawFeature): ?Feature => {
          if (!config.isProduction) {
            debug(feature.flagID, feature.evalDebugLog);
          }
          const flag: Flag = findFlagById(feature.flagID)(FLAGS);
          if (!flag) {
            return false;
          }
          return {
            key: flag.name,
            enabled: isRawFeatureEnabled(feature),
            variantKey: feature.variantKey,
            variantID: feature.variantID,
            variantAttachment: feature.variantAttachment,
            segmentID: feature.segmentID
          };
        }
      )
      .filter(elem => typeof elem === 'object');

    return {
      ...state,
      loading: false,
      loaded: true,
      features: [...state.features, ...features]
    };
  },
  [LOAD_FAIL](state: State, action): State {
    debug(LOAD_FAIL, action);
    return { ...state, loading: false, loaded: false };
  }
});

export function isFeatureEnabled(
  features: State,
  featureName: string
): boolean {
  const isEnabled = (() => {
    if (features.features.length === 0) {
      return false;
    }
    const feature = findFeatureByKey(featureName)(features.features);
    if (!feature) {
      return false;
    }
    return feature.enabled;
  })();

  debug('isFeatureEnabled?', featureName, isEnabled);
  return isEnabled;
}

export function getFeatureFromState(
  features: State,
  featureName: string
): ?Feature {
  return findFeatureByKey(featureName)(features.features);
}

const buildBatchRequest = (identifier, userLoggedIn, user) => {
  const evalRequest = new EvaluationBatchRequest([
    {
      entityId: identifier,
      entityType: 'user',
      entityContext: {
        userLoggedIn: Boolean(userLoggedIn).toString(),
        identifier,
        ...user
      }
    }
  ]);
  evalRequest.flagIDs = flagIDs;
  if (!config.isProduction) {
    evalRequest.enableDebug = true;
  }
  return evalRequest;
};

export function load(user: ?User, fingerprint) {
  const apiInstance = new EvaluationApi();
  let userLoggedIn = false;
  let identifier = fingerprint.hash;
  if (user) {
    userLoggedIn = !!user.id;
    identifier = user.id ? user.id : identifier;
  }
  return (dispatch: Action => void, getState: () => GlobalState) => {
    if (!getState().features.loading && !getState().features.loaded) {
      return dispatch({
        types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
        promise: () =>
          new Promise((resolve, reject) => {
            apiInstance.postEvaluationBatch(
              buildBatchRequest(identifier, userLoggedIn, user),
              (error, data) => {
                if (error) {
                  reject(new Error('Error getting features from FEATURE API'));
                } else {
                  resolve({ ...data });
                }
              }
            );
          })
      });
    }
    return Promise.resolve(getState().features.features);
  };
}
